/**
 * Add or edit a 24-Hour Line member (docs/plans/oncall-line-plan.md 7.5 POST
 * /members and PATCH /members/:id, 7.7, 6.2; WP-11). Admin only (the Team
 * tab sits behind the shell's admin guard and every route carries
 * requireOncallAdmin).
 *
 * Fields: the name, the US cell (sent as E.164; the server normalizes and
 * judges it again: 422 for a number it refuses, 409 ONCALL_MEMBER_EXISTS for
 * a number already on the list), and the optional link to a SiteOps user
 * (who may then use "Call back" in SiteOps, plan 6.2; 409
 * ONCALL_USER_ALREADY_LINKED when that user is linked to another member).
 * An edit sends only what changed; a changed number clears the last test
 * result on the server (it tested the old number).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { apiErrorMessage, createMember, updateMember } from '../../api/oncallApi';
import type { CreateMemberInput, OncallMember, UpdateMemberInput } from '../../api/oncallApi';
import { formatPhone } from '../../lib/format';
import { toUsE164, usNumberProblem } from './phone';
import { listSiteUsers, userLabel } from './siteUsers';
import type { SiteUser } from './siteUsers';

/** The longest display name the server takes (members.js MAX_DISPLAY_NAME_LENGTH). */
export const MAX_DISPLAY_NAME_LENGTH = 100;

export interface MemberFormModalProps {
  isOpen: boolean;
  /** The member being edited; absent to add one. */
  member?: OncallMember | null;
  /** The line's own number (a member may not be it). */
  lineNumber: string | null;
  /** The site whose users the link list offers. */
  siteId: string;
  /** The signed-in admin, always offered as a link target. */
  currentUser: { id: string; full_name?: string | null; email?: string | null } | null;
  onClose: () => void;
  /** Called with the saved row after a successful create or edit. */
  onSaved: (member: OncallMember, mode: 'add' | 'edit') => void;
}

const NOT_LINKED = '';

interface Errors {
  name?: string;
  phone?: string;
  form?: string;
}

export default function MemberFormModal({
  isOpen,
  member,
  lineNumber,
  siteId,
  currentUser,
  onClose,
  onSaved,
}: MemberFormModalProps) {
  const editing = Boolean(member);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [userId, setUserId] = useState<string>(NOT_LINKED);
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<SiteUser[] | null>(null);
  const [usersFailed, setUsersFailed] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  // A fresh form each time the modal opens (or opens on another member).
  useEffect(() => {
    if (!isOpen) return;
    setName(member?.display_name ?? '');
    setPhone(member?.phone_e164 ? formatPhone(member.phone_e164) : '');
    setUserId(member?.user_id ?? NOT_LINKED);
    setErrors({});
    setSaving(false);
  }, [isOpen, member]);

  // The site's users for the link list; a failure leaves the short list.
  useEffect(() => {
    if (!isOpen || !siteId) return undefined;
    let cancelled = false;
    setUsers(null);
    setUsersFailed(false);
    listSiteUsers(siteId)
      .then((rows) => {
        if (!cancelled) setUsers(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setUsers([]);
          setUsersFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, siteId]);

  const userOptions = useMemo(() => {
    const options: Array<{ id: string; label: string }> = [];
    const seen = new Set<string>();
    const add = (id: string | null | undefined, label: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      options.push({ id, label });
    };
    if (currentUser?.id) {
      add(currentUser.id, `Me: ${userLabel({ full_name: currentUser.full_name ?? '', email: currentUser.email ?? '' })}`);
    }
    if (member?.user_id) add(member.user_id, member.user_name?.trim() || 'The linked user');
    for (const u of users ?? []) add(u.id, userLabel(u));
    return options;
  }, [currentUser, member, users]);

  const validate = (): Errors => {
    const next: Errors = {};
    const trimmed = name.replace(/\s+/g, ' ').trim();
    if (!trimmed) next.name = 'Enter the person\'s name.';
    else if (trimmed.length > MAX_DISPLAY_NAME_LENGTH) next.name = `At most ${MAX_DISPLAY_NAME_LENGTH} characters.`;
    const phoneProblem = usNumberProblem(phone, { lineNumber });
    if (phoneProblem) next.phone = phoneProblem;
    return next;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const found = validate();
    setErrors(found);
    if (found.name || found.phone) {
      if (found.name) nameRef.current?.focus();
      return;
    }
    const displayName = name.replace(/\s+/g, ' ').trim();
    const e164 = toUsE164(phone) as string;
    const linked = userId === NOT_LINKED ? null : userId;

    setSaving(true);
    try {
      if (member) {
        const patch: UpdateMemberInput = {};
        if (displayName !== member.display_name) patch.display_name = displayName;
        if (e164 !== member.phone_e164) patch.phone_e164 = e164;
        if (linked !== (member.user_id ?? null)) patch.user_id = linked;
        if (Object.keys(patch).length === 0) {
          setSaving(false);
          onClose();
          return;
        }
        const saved = await updateMember(member.id, patch);
        onSaved(saved, 'edit');
      } else {
        const input: CreateMemberInput = { display_name: displayName, phone_e164: e164 };
        if (linked) input.user_id = linked;
        const saved = await createMember(input);
        onSaved(saved, 'add');
      }
    } catch (err) {
      setErrors({ form: apiErrorMessage(err, editing ? 'The member could not be saved.' : 'The member could not be added.') });
      setSaving(false);
    }
  };

  const phoneChanged = editing && member?.phone_e164 && toUsE164(phone) !== member.phone_e164;

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose} title={editing ? 'Edit member' : 'Add member'} maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} noValidate data-testid="oncall-member-form" className="space-y-4">
        {errors.form && (
          <p role="alert" data-testid="oncall-member-form-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {errors.form}
          </p>
        )}

        <div>
          <label htmlFor="oncall-member-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
            Name
          </label>
          <input
            id="oncall-member-name"
            ref={nameRef}
            type="text"
            value={name}
            maxLength={MAX_DISPLAY_NAME_LENGTH + 20}
            autoComplete="off"
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={errors.name ? 'oncall-member-name-error' : undefined}
            className={`${fieldCls} w-full`}
          />
          {errors.name && (
            <p id="oncall-member-name-error" className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="oncall-member-phone" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
            Cell phone (US)
          </label>
          <input
            id="oncall-member-phone"
            type="tel"
            inputMode="tel"
            value={phone}
            autoComplete="off"
            placeholder="(555) 234-5678"
            onChange={(e) => {
              setPhone(e.target.value);
              if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
            }}
            aria-invalid={errors.phone ? true : undefined}
            aria-describedby={errors.phone ? 'oncall-member-phone-error' : 'oncall-member-phone-hint'}
            className={`${fieldCls} w-full`}
          />
          {errors.phone ? (
            <p id="oncall-member-phone-error" className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.phone}
            </p>
          ) : (
            <p id="oncall-member-phone-hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {phoneChanged
                ? 'A new number clears the last test result. Send a test call after saving.'
                : 'The line rings this phone. Only line admins see the full number.'}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="oncall-member-user" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
            SiteOps user (optional)
          </label>
          <select
            id="oncall-member-user"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            aria-describedby="oncall-member-user-hint"
            className={`${fieldCls} w-full`}
          >
            <option value={NOT_LINKED}>Not linked</option>
            {userOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <p id="oncall-member-user-hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {users === null
              ? 'Loading the site\'s users…'
              : usersFailed
                ? 'The site\'s user list could not be loaded. You can link yourself or leave the member unlinked.'
                : 'A linked member can call a participant back from SiteOps. Each user links to one member.'}
          </p>
        </div>

        {!editing && (
          <p className="rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
            A new member is active at once and goes last in the order. Send them a test call, and ask them to save the
            line&apos;s number as a contact that can ring through Do Not Disturb.
          </p>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" onClick={onClose} disabled={saving} className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}>
            Cancel
          </button>
          <button type="submit" disabled={saving} data-testid="oncall-member-save" className={primaryBtnCls}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {editing ? 'Save' : 'Add member'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
